import { Badge } from '@oysterjs/ui/badge';
import {
  AdminAnalyticsAmsWriteSourceType,
  type AdminAnalyticsReportFieldsFragment
} from '~/api/graphql';
import { integrationName } from '~/lib/integrations';
import { pluralize } from '~/lib/pluralize';
import {
  formatNumber,
  longDateFormatter,
  utcDateFormatter,
  VISIBLE_PERIODS
} from '~/components/settings/analytics/analytics-constants';
import {
  EmptyMessage,
  KpiTile,
  SectionHeading,
  type TrendBar,
  TrendBars
} from './analytics-card-parts';

type AmsWrites = AdminAnalyticsReportFieldsFragment['amsWrites'];
type AmsWritePeriod = AmsWrites['overTime'][number];

const SOURCE_LABELS: Record<AdminAnalyticsAmsWriteSourceType, string> = {
  [AdminAnalyticsAmsWriteSourceType.Cara]: 'Cara',
  [AdminAnalyticsAmsWriteSourceType.VoiceAi]: 'Voice AI'
};

export const AmsWritesSection = ({
  amsWrites,
  voiceAiNote
}: {
  amsWrites: AmsWrites;
  /** Names the filters that leave Voice AI writes out. Without it the note is generic. */
  voiceAiNote?: string;
}) => {
  const heading = (
    <>
      <SectionHeading
        title="AMS and CRM writes"
        subtitle="Records Cara and Voice AI wrote to your connected systems"
      />
      {(voiceAiNote || amsWrites.voiceAiExcluded) && (
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          {voiceAiNote ??
            'Voice AI writes are hidden under this filter because they have no user or workspace.'}
        </p>
      )}
    </>
  );

  if (!amsWrites.hasIntegration && amsWrites.recordingStartedAt == null) {
    return (
      <>
        {heading}
        <EmptyMessage>No AMS or CRM connected.</EmptyMessage>
      </>
    );
  }

  return (
    <>
      {heading}
      {amsWrites.succeeded + amsWrites.failed === 0 ? (
        <EmptyMessage>No AMS or CRM writes in this period.</EmptyMessage>
      ) : (
        <>
          <AmsWritesSummary amsWrites={amsWrites} />
          <AmsWritesTrend periods={amsWrites.overTime} />
        </>
      )}
      {amsWrites.recordingStartedAt != null && (
        <p className="mt-4 text-[11px] text-neutral-400 dark:text-neutral-500">
          Recording started {longDateFormatter.format(new Date(amsWrites.recordingStartedAt))}
        </p>
      )}
    </>
  );
};

const AmsWritesSummary = ({ amsWrites }: { amsWrites: AmsWrites }) => {
  const integrations = amsWrites.byIntegration;
  const onlyIntegration = integrations.length === 1 ? integrations[0] : undefined;
  const recordTypes = amsWrites.byRecordType.filter((r) => r.succeeded > 0);

  return (
    <div className="mt-4 grid items-start gap-4 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
      <KpiTile
        label="Records written"
        value={formatNumber(amsWrites.succeeded)}
        detail={onlyIntegration && `to ${integrationName(onlyIntegration.integrationType)}`}
      />
      <div className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
        {amsWrites.failed > 0 && <FailedBadge count={amsWrites.failed} />}
        {recordTypes.length > 0 && (
          <p>{recordTypes.map((r) => `${r.label} ${formatNumber(r.succeeded)}`).join(' · ')}</p>
        )}
        {amsWrites.bySource.length > 0 && (
          <p className="text-neutral-500 dark:text-neutral-400">
            {amsWrites.bySource
              .map((s) => `${SOURCE_LABELS[s.source]} ${formatNumber(s.succeeded)}`)
              .join(' · ')}
          </p>
        )}
        {integrations.length > 1 && (
          <ul className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 divide-y divide-neutral-100 dark:divide-neutral-700">
            {integrations.map((i) => (
              <li
                key={i.integrationType}
                className="col-span-3 grid grid-cols-subgrid items-center justify-items-start py-1.5"
              >
                <span className="truncate">{integrationName(i.integrationType)}</span>
                {i.failed > 0 && <FailedBadge count={i.failed} />}
                <span className="col-start-3 justify-self-end tabular-nums font-medium text-neutral-900 dark:text-white">
                  {formatNumber(i.succeeded)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const AmsWritesTrend = ({ periods }: { periods: AmsWritePeriod[] }) => {
  const visible = periods.slice(-VISIBLE_PERIODS);
  if (visible.length === 0) {
    return null;
  }
  const notRecorded = visible.filter((p) => !p.recorded).length;
  const periodCount = `${visible.length} ${pluralize(visible.length, 'period')}`;
  const summary = `Writes per period, ${periodCount}${notRecorded > 0 ? `, ${notRecorded} not recorded yet` : ''}`;
  const bars = visible.map((period): TrendBar => {
    const periodStart = new Date(period.periodStart);
    return {
      periodStart,
      count: period.succeeded + period.failed,
      recorded: period.recorded,
      title: period.recorded
        ? `${utcDateFormatter.format(periodStart)}: ${formatNumber(period.succeeded)} written, ${formatNumber(period.failed)} failed`
        : 'Not recorded yet'
    };
  });

  return (
    <div className="mt-5">
      <TrendBars bars={bars} label={summary} />
      {notRecorded > 0 && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
          <span className="inline-block size-2.5 rounded-sm border border-dashed border-neutral-300 bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800" />
          Not recorded yet
        </p>
      )}
    </div>
  );
};

const FailedBadge = ({ count }: { count: number }) => (
  <Badge color="rose">{formatNumber(count)} failed</Badge>
);
